import { Decorative } from "@faire/design-tokens";
import {
  getWindow,
  getWindowOrThrow,
} from "@faire/web/common/globals/getWindow";
import { optimizeImageUrl } from "@faire/web/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web/common/localization";
import { IRawQueryParamsProps } from "@faire/web/common/query-params/IQueryParamsProps";
import { withQueryParams } from "@faire/web/common/query-params/withQueryParams";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { Column } from "@faire/web/slate/Layout";
import { LoadingSpinner } from "@faire/web/slate/LoadingSpinner";
import { FaireLogo } from "@faire/web/slate/logos/FaireLogo";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useIntersectionObserver } from "@faire/web/ui/hooks/useIntersectionObserver";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import { observer } from "mobx-react";
import React, { useCallback, useEffect, useRef } from "react";
import { withRouter, useHistory } from "react-router-dom";
import styled from "styled-components";

import { FakeAnchor } from "@faire/retailer/components/FakeAnchor";
import { FooterSEOContent } from "@faire/retailer/components/Footer/FooterSEOContent";
import { GeographicMap } from "@faire/retailer/components/GeographicMap";
import { HeaderItemCss } from "@faire/retailer/components/Header/styled";
import { AccessibilityLink } from "@faire/retailer/lib/AccessibilityLink";
import { home } from "@faire/retailer/paths";
import { MakerSignUpState } from "@faire/retailer/views/apply/MakerApplication/MakerSignUpState";
import { BrandsLandingPageNav } from "@faire/retailer/views/brands/landing-page/BrandsLandingPageNav";
import { BrandsLandingPageTopSection } from "@faire/retailer/views/brands/landing-page/BrandsLandingPageTopSection";

import { BrandsLandingPageBottomSection } from "./BrandsLandingPageBottomSection";
import { BrandsLandingPageCaseStudies } from "./BrandsLandingPageCaseStudies";
import { BrandsLandingPageFaireMarket } from "./BrandsLandingPageFaireMarket";
import { BrandsLandingPageFaireMarketBanner } from "./BrandsLandingPageFaireMarketBanner";
import { BrandsLandingPageFaireMarketShowcase } from "./BrandsLandingPageFaireMarketShowcase";
import { BrandsLandingPageIntro } from "./BrandsLandingPageImprovementsIntro";
import { BrandsLandingPageState } from "./BrandsLandingPageState";
import { BrandsLandingPageTestimonials } from "./BrandsLandingPageTestimonials";
import { BrandsLandingPageTestimonialsSwipe } from "./BrandsLandingPageTestimonialsSwipe";
import {
  BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE,
  getIsShowingCaseStudies,
} from "./const";
import { getIntros } from "./intros";

const FAIRE_MARKET_SECTION = "faire-market-section";

interface IRouteParams {
  brandToken: string;
}

interface IProps
  extends IRawQueryParamsProps<{ email_address?: string }, IRouteParams> {
  makerApplicationState?: MakerSignUpState;
}

const BrandsLandingPageBase: React.FC<IProps> = (props) => {
  const history = useHistory();
  const { strictLocalize } = useStrictLocalization();
  const viewState = useRefFrom(
    () =>
      new BrandsLandingPageState(
        history,
        props.match.params.brandToken,
        props.queryParams.get("email_address") ?? undefined
      )
  ).current;

  const categoryInclusiveReference = useRef<HTMLDivElement>(null);

  const onScrollHandler = useCallback(() => {
    if (categoryInclusiveReference.current) {
      const { offsetTop, clientHeight } = categoryInclusiveReference.current;
      const isBelowSignup =
        getWindowOrThrow().scrollY > offsetTop + clientHeight;

      if (viewState.showBanner !== isBelowSignup) {
        viewState.showBanner = isBelowSignup;
      }
    }
  }, [categoryInclusiveReference, viewState]);

  useEffect(() => {
    getWindow()?.addEventListener("scroll", onScrollHandler);

    viewState.init();

    props.makerApplicationState &&
      viewState.setMakerApplicationState(props.makerApplicationState);

    return () => {
      getWindow()?.removeEventListener("scroll", onScrollHandler);
    };
  }, [onScrollHandler, props.makerApplicationState, viewState]);

  const isSSR = useIsSSR();

  const ref = useIntersectionObserver((entry) => {
    viewState.setIsMarketSectionVisible(entry.isIntersecting);
  });

  if (viewState.isLoading) {
    return (
      <Column align="center">
        <LoadingSpinner size="large" />
      </Column>
    );
  }

  return (
    <>
      {viewState.displayFaireMarket && !viewState.showBanner ? (
        <BrandsLandingPageFaireMarketBanner
          faireMarketSectionId={FAIRE_MARKET_SECTION}
        />
      ) : null}
      <StyledHeaderWrapper>
        <AccessibilityLink
          to={home()}
          id="faire-logo-link"
          aria-label={strictLocalize({
            defaultMessage: "Go to Faire homepage",
            description: {
              text: "Description describing to screen reader users what happens when they click the Faire logo.",
            },
          })}
        >
          <Spacer width="2x" />
          <FaireLogo inverse />
        </AccessibilityLink>
      </StyledHeaderWrapper>
      <BrandsLandingPageNav viewState={viewState} />
      <Container>
        <BrandsLandingPageTopSection
          categoryInclusiveReference={categoryInclusiveReference}
          viewState={viewState}
        />
        <ResponsiveContentV2
          mobile={
            <MobileBannerImage
              src={optimizeImageUrl(
                { url: BRANDS_LANDING_PAGE_BELOW_HEADER_IMAGE },
                isSSR
              )}
            />
          }
        />
        <Spacer
          height={{
            mobileAndAbove: "3x",
            desktopAndAbove: 96,
          }}
        />

        {getIntros(isSSR, strictLocalize).map((intro) => (
          <div key={intro.index}>
            <BrandsLandingPageIntro
              index={intro.index}
              header={intro.header}
              description={intro.description}
              richContent={intro.richContent}
              reverse={intro.reverse}
              startTracking={viewState.showBanner}
              eventKey={intro.eventKey}
            />
            <Spacer
              height={{
                mobileAndAbove: "3x",
                tabletAndAbove: "6x",
              }}
            />
          </div>
        ))}
        <Spacer
          height={{
            mobileAndAbove: "1x",
            tabletAndAbove: "4x",
            desktopAndAbove: 96,
          }}
        />
        {viewState.displayFaireMarket ? (
          <div ref={ref} style={{ width: "100%" }}>
            <FakeAnchor id={FAIRE_MARKET_SECTION} />
            <BrandsLandingPageFaireMarketShowcase />
            <BrandsLandingPageFaireMarket onClick={viewState.goToForm} />
          </div>
        ) : null}

        {getIsShowingCaseStudies() ? <BrandsLandingPageCaseStudies /> : null}

        <ResponsiveContentV2 mobile={<BrandsLandingPageTestimonialsSwipe />} />
        <GeographicMap showCaseStudies={getIsShowingCaseStudies()} />
        <BrandsLandingPageTestimonials
          isPersonalized={viewState.isPersonalized}
        />
        <BrandsLandingPageBottomSection
          onButtonClick={viewState.goToForm}
          isPersonalized={viewState.isPersonalized}
          calendlyLink={viewState.calendlyLink}
        />

        <FooterSEOContent />
      </Container>
    </>
  );
};

export const BrandsLandingPage = withRouter(
  withQueryParams(observer(BrandsLandingPageBase))
);

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  align-items: center;
  position: relative;
  overflow-x: hidden;
`;

const MobileBannerImage = styled.img`
  height: 112px;
  width: 100%;
  object-fit: cover;
  object-position: center;
`;

const StyledHeaderWrapper = styled.div`
  background-color: ${Decorative.shade.yellow};
  ${HeaderItemCss};
  padding: ${getSpacing(0, 0, 0, "3x")};
  justify-content: start;

  ${responsiveMediaQueries.mobile} {
    justify-content: center;
  }
`;
